import { clone } from "@bufbuild/protobuf";
import { titleCase } from "scule";
import { AssetType, ConversionType, LinkSchema, type AssetRef, type Bundle, type Link, type Module, type ModuleTemplate, type Suite, type TemplateRef } from "#gen/bundles/v1/bundle_pb.js";
import { BUTTON_ANCHOR_BASE_NAME, FORM_ANCHOR_BASE_NAME, IMAGE_BUTTON_ANCHOR_BASE_NAME, isButtonAnchor, isFormAnchor, isImageButtonAnchor } from "#lib/componentAnchors.js";
import { deepClone } from "#lib/deepClone.js";

export const APP_ANCHOR_NAME = "apps";
export const CONVERSION_ANCHOR_NAME = "conversion";
export const SECTION_1_ANCHOR_NAME = "section1";
export const SECTION_2_ANCHOR_NAME = "section2";
export const RIGHT_ANCHOR_NAME = "right";
export const LEFT_ANCHOR_NAME = "left";

export const ENTRY_ASSET_TYPES = new Set([AssetType.CODE, AssetType.CODE_V3, AssetType.CODE_BATCH, AssetType.SHORT_URL, AssetType.ENTRYPOINT_BATCH, AssetType.ENTRYPOINT_BATCH_REF]);
export const BATCH_ASSET_TYPES = new Set([AssetType.CODE_BATCH, AssetType.ENTRYPOINT_BATCH]);

const EDGE_PRIORITY = [SECTION_1_ANCHOR_NAME, SECTION_2_ANCHOR_NAME, CONVERSION_ANCHOR_NAME, APP_ANCHOR_NAME, BUTTON_ANCHOR_BASE_NAME, IMAGE_BUTTON_ANCHOR_BASE_NAME, FORM_ANCHOR_BASE_NAME, LEFT_ANCHOR_NAME, RIGHT_ANCHOR_NAME];
export const KEY_SEPARATOR = ":";

export interface BundleNode<T extends AssetRef | TemplateRef> {
	key: string;
	parent?: string;
	ref: T;
	linksOut: Link[];
	linksIn: Link[];
	displayName: string;
}

type Nodes<T extends AssetRef | TemplateRef> = Map<string, BundleNode<T>>;

interface NodeAndLevel<T extends AssetRef | TemplateRef> {
	node: BundleNode<T>;
	level: number;
}

export function generateId(suite: Suite, type: AssetType): string {
	const prefix = AssetType[type].toLowerCase();
	let i = 1;
	let id = `${prefix}-${i}`;
	while (suite.assets[id]) {
		id = `${prefix}-${++i}`;
	}
	return id;
}

export function sanitizeAnchor(id: string | null | undefined): string {
	if (id?.startsWith(`${APP_ANCHOR_NAME}-`)) {
		return APP_ANCHOR_NAME;
	}
	return id ?? "";
}

export function isBatchEntrypoint(ref: AssetRef): boolean {
	return ref.type === AssetType.ENTRYPOINT_BATCH || ref.type === AssetType.CODE_BATCH;
}

function sortConversionNodes<T extends AssetRef | TemplateRef>(a: BundleNode<T>, b: BundleNode<T>): number {
	const aIndex = a.linksIn.findIndex(link => link.sourceAnchor === CONVERSION_ANCHOR_NAME);
	const bIndex = b.linksIn.findIndex(link => link.sourceAnchor === CONVERSION_ANCHOR_NAME);
	if (aIndex === bIndex) {
		return a.displayName.localeCompare(b.displayName);
	}
	return bIndex - aIndex;
}

function getAnchorPriority(anchor: string | undefined): number {
	if (anchor && EDGE_PRIORITY.includes(anchor)) {
		return EDGE_PRIORITY.indexOf(anchor);
	}
	if (isButtonAnchor(anchor)) {
		return EDGE_PRIORITY.indexOf(BUTTON_ANCHOR_BASE_NAME);
	}
	if (isImageButtonAnchor(anchor)) {
		return EDGE_PRIORITY.indexOf(IMAGE_BUTTON_ANCHOR_BASE_NAME);
	}
	if (isFormAnchor(anchor)) {
		return EDGE_PRIORITY.indexOf(FORM_ANCHOR_BASE_NAME);
	}
	return EDGE_PRIORITY.length;
}

function priorityOrder(a: Link, b: Link): number {
	const aAnchor = sanitizeAnchor(a.sourceAnchor);
	const bAnchor = sanitizeAnchor(b.sourceAnchor);
	return getAnchorPriority(aAnchor) - getAnchorPriority(bAnchor);
}

export class BundleDag<T extends AssetRef | TemplateRef> {
	nodes: Nodes<T>;
	roots: BundleNode<T>[];
	modules: Record<string, BundleDag<T>>;
	conversions: BundleNode<T>[];
	apps: BundleNode<T>[];

	constructor() {
		this.nodes = new Map();
		this.conversions = [];
		this.apps = [];
		this.roots = [];
		this.modules = {};
	}

	clone(): BundleDag<T> {
		const newDag: BundleDag<T> = new BundleDag<T>();
		newDag.nodes = deepClone(this.nodes);
		newDag.roots = this.roots.map(root => newDag.nodes.get(root.key)!);
		for (const [key, module] of Object.entries(this.modules)) {
			newDag.modules[key] = module.clone();
		}
		newDag.conversions = this.conversions.map(node => newDag.nodes.get(node.key)!);
		newDag.apps = this.apps.map(node => newDag.nodes.get(node.key)!);
		return newDag;
	}

	addNode(key: string, ref: T, parent?: string) {
		const qualified = qualifyKey(key, parent);
		const displayName = ref.displayName ? ref.displayName : titleCase(ref.conversionComponent);
		this.nodes.set(qualified, {
			key: qualified,
			parent,
			displayName,
			ref,
			linksIn: [],
			linksOut: [],
		});
	}

	addLink(link: Link, parent?: string, spliceIndex?: number) {
		const edge = parent ? clone(LinkSchema, link) : link;
		edge.source = qualifyKey(link.source, parent);
		edge.destination = qualifyKey(link.destination, parent);
		if (spliceIndex !== undefined && spliceIndex >= 0) {
			this.nodes.get(edge.source)?.linksOut.splice(spliceIndex, 0, edge);
		} else {
			this.nodes.get(edge.source)?.linksOut.push(edge);
		}
		this.nodes.get(edge.destination)?.linksIn.push(edge);
	}

	replaceLink(oldLink: Link, newLink: Link, parent?: string) {
		const qualifiedOldSource = qualifyKey(oldLink.source, parent);
		const qualifiedOldDestination = qualifyKey(oldLink.destination, parent);
		const qualifiedNewSource = qualifyKey(newLink.source, parent);
		const qualifiedNewDestination = qualifyKey(newLink.destination, parent);
		const oldSourceNode = this.nodes.get(qualifiedOldSource);
		if (oldSourceNode) {
			oldSourceNode.linksOut = oldSourceNode.linksOut.filter(link => !(link.source === qualifiedOldSource && link.destination === qualifiedOldDestination));
		}
		const oldDestinationNode = this.nodes.get(qualifiedOldDestination);
		if (oldDestinationNode) {
			oldDestinationNode.linksIn = oldDestinationNode.linksIn.filter(link => !(link.source === qualifiedOldSource && link.destination === qualifiedOldDestination));
		}
		const edge = parent ? clone(LinkSchema, newLink) : newLink;
		edge.source = qualifiedNewSource;
		edge.destination = qualifiedNewDestination;
		this.nodes.get(edge.source)?.linksOut.push(edge);
		this.nodes.get(edge.destination)?.linksIn.push(edge);
	}

	addModule(key: string, nodes: Record<string, T>, links: Link[], entryPoint: string) {
		const subdag = parseDag(nodes, links, key);
		const node = subdag.nodes.get(qualifyKey(entryPoint, key));
		subdag.roots = node ? [node] : [];
		this.modules[key] = subdag;
	}

	getNode(key: string): BundleNode<T> | undefined {
		const dag = this.findDagForKey(key);
		return dag.nodes.get(key);
	}

	getModule(key: string): BundleDag<T> | undefined {
		return this.modules[key];
	}

	findAllEntryPoints(type?: AssetType): BundleNode<T>[] {
		const entryPoints = this.roots;
		if (!type) {
			return entryPoints;
		}
		return entryPoints.filter(root => type === root.ref.type);
	}

	findPrimaryEntryPoint(): BundleNode<T> | undefined {
		return this.roots[0];
	}

	findInitialAsset(): BundleNode<T> | undefined {
		const key = this.roots[0]?.linksOut[0]?.destination;
		if (key) {
			return this.getNode(key);
		}
		return undefined;
	}

	findFinalAsset(): BundleNode<T> | undefined {
		const nodes = this.bfs();
		for (let i = nodes.length - 1; i >= 0; i--) {
			if (nodes[i].linksOut.length === 0) {
				return nodes[i];
			}
		}
		return undefined;
	}

	findAllConversionModules(): BundleNode<T>[] {
		const conversions = Array.from(this.nodes.values()).filter(node => node.ref.conversionType !== ConversionType.UNSPECIFIED);
		for (const dag of Object.values(this.modules)) {
			conversions.push(...dag.findAllConversionModules());
		}
		return conversions.sort(sortConversionNodes);
	}

	findConversionsByAssetKey(assetKey: string): BundleNode<T>[] {
		return this.findModulesFromAssetKey(assetKey, CONVERSION_ANCHOR_NAME);
	}

	findAppsByAssetKey(assetKey: string): BundleNode<T>[] {
		return this.findModulesFromAssetKey(assetKey, APP_ANCHOR_NAME);
	}

	findModulesFromAssetKey(assetKey: string, anchor?: string): BundleNode<T>[] {
		const node = this.nodes.get(assetKey);
		if (!node) {
			return [];
		}
		const links = anchor ? node.linksOut.filter(link => link.sourceAnchor === anchor) : node.linksOut;
		return links.flatMap(link => {
			const destination = this.nodes.get(link.destination);
			return destination ? [destination] : [];
		});
	}

	findDestinationsFromAnchors(assetKey: string, anchors: readonly string[]): BundleNode<T>[] {
		const anchorSet = new Set(anchors);
		const destinations: Map<string, BundleNode<T>> = new Map();
		for (const link of this.nodes.get(assetKey)?.linksOut ?? []) {
			const destination = anchorSet.has(link.sourceAnchor) ? this.nodes.get(link.destination) : undefined;
			if (destination) {
				destinations.set(destination.key, destination);
			}
		}
		return [...destinations.values()];
	}

	findNodeOfType(type: AssetType): BundleNode<T> | undefined {
		return Array.from(this.nodes.values()).find(node => node.ref.type === type);
	}

	findNodesOfType(type: AssetType): BundleNode<T>[] {
		return Array.from(this.nodes.values()).filter(node => node.ref.type === type);
	}

	hasNodeOfType(type: AssetType): boolean {
		return Array.from(this.nodes.values()).some(node => node.ref.type === type);
	}

	hasAnyNodeOfType(types: AssetType[]): boolean {
		return Array.from(this.nodes.values()).some(node => types.includes(node.ref.type));
	}

	hasBatchEntrypoint(): boolean {
		return this.hasAnyNodeOfType([AssetType.ENTRYPOINT_BATCH, AssetType.CODE_BATCH]);
	}

	hasAgnosticEntrypoint(): boolean {
		return this.hasNodeOfType(AssetType.ENTRYPOINT_BATCH);
	}

	isModuleRoot(key: string): boolean {
		const node = this.getNode(key);
		if (node && node.parent) {
			return this.modules[node.parent]?.roots.some(root => root.key === node.key);
		}
		return false;
	}

	getContainingModule(key: string): string | undefined {
		const node = this.getNode(key);
		return node?.parent;
	}

	getNodesByLevel(): Record<number, BundleNode<T>[]> {
		const levels: Record<number, BundleNode<T>[]> = {};
		const q: NodeAndLevel<T>[] = this.roots.map(node => ({ node, level: 0 }));
		while (q.length > 0) {
			const { level, node } = q.shift()!;
			if (!levels[level]) {
				levels[level] = [];
			}
			levels[level].push(node);
			if (node.linksOut) {
				node.linksOut.forEach(link => {
					const childNode = this.nodes.get(link.destination);
					if (childNode) {
						q.push({ node: childNode, level: level + 1 });
					}
				});
			}
		}
		return levels;
	}

	bfs(from: BundleNode<T>[] = this.roots): BundleNode<T>[] {
		const res: BundleNode<T>[] = [];
		const q: BundleNode<T>[] = [];
		const visited: Set<string> = new Set();
		for (const s of from ?? this.roots) {
			visited.add(s.key);
			q.push(s);
		}
		while (q.length > 0) {
			const curr = q.shift()!;
			res.push(curr);
			if (this.needsExpansion(curr)) {
				res.push(...this.modules[curr.key].bfs());
			}
			curr.linksOut.sort(priorityOrder);
			for (const x of curr.linksOut) {
				const node = this.nodes.get(x.destination);
				if (node && !visited.has(node.key)) {
					visited.add(node.key);
					q.push(node);
				}
			}
		}
		return res;
	}

	private findDagForKey(key: string): BundleDag<T> {
		let dag: BundleDag<T> | undefined;
		const parts = key.split(KEY_SEPARATOR);
		for (let i = 0; i < parts.length - 1; i++) {
			dag = (dag || this).modules[parts.slice(0, i + 1).join(KEY_SEPARATOR)];
		}
		return dag || this;
	}

	private needsExpansion(node: BundleNode<T>): boolean {
		return node.ref.type === AssetType.MODULE && node.key in this.modules;
	}
}

export function parseDagFromSuite(suite: Suite, modules?: Record<string, Module>): BundleDag<AssetRef> {
	const dag = parseDag(suite.assets, suite.links);
	if (modules) {
		for (const [key, module] of Object.entries(modules)) {
			dag.addModule(key, module.assets, module.links, module.entryPoint);
		}
	}
	dag.roots = Array.from(dag.nodes.values()).filter(node => node.linksIn.length === 0);
	return dag;
}

export function parseDagFromBundle(bundle: Bundle, modules?: Record<string, ModuleTemplate>): BundleDag<TemplateRef> {
	const dag = parseDag(bundle.templates, bundle.links);
	if (modules) {
		for (const [key, module] of Object.entries(modules)) {
			dag.addModule(key, module.templates, module.links, module.entryPoint);
		}
	}
	dag.roots = Array.from(dag.nodes.values()).filter(node => node.linksIn.length === 0);
	return dag;
}

export function qualifyKey(key: string, parent?: string): string {
	const prefix = parent ? `${parent}${KEY_SEPARATOR}` : "";
	if (key.startsWith(prefix)) {
		return key;
	}
	return parent ? `${prefix}${key}` : key;
}

export function relativeKey(key: string, parent?: string): string {
	return parent ? key.replace(`${parent}${KEY_SEPARATOR}`, "") : key;
}

function parseDag<T extends AssetRef | TemplateRef>(nodes: Record<string, T>, links: Link[], parent?: string): BundleDag<T> {
	const dag: BundleDag<T> = new BundleDag();
	for (const key in nodes) {
		dag.addNode(key, nodes[key], parent);
	}
	for (const link of links) {
		if (!link.destination) {
			continue;
		}
		dag.addLink(link, parent);
		if (nodes[link.source]?.type === AssetType.LANDING_PAGE) {
			if (link.sourceAnchor === CONVERSION_ANCHOR_NAME) {
				if (dag.nodes.has(link.destination)) {
					dag.conversions.push(dag.nodes.get(link.destination)!);
				}
			} else if (link.sourceAnchor === APP_ANCHOR_NAME) {
				if (dag.nodes.has(link.destination)) {
					dag.apps.push(dag.nodes.get(link.destination)!);
				}
			}
		}
	}
	return dag;
}
